import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import type { ReactNode } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { ContentDetailView } from '@/components/content/ContentDetailView';
import type { ApiContent } from '@/lib/api/types';
import { useSheetManager } from '@/lib/ui';
import { useSession } from '@/lib/auth-client';
import { contentQueryKeys, useContentItem } from './useContent';
interface ShowContentDetailOptions {
  showMap?: boolean;
  showEditButton?: boolean;
  keepOpenOnRouteChange?: boolean;
  /**
   * Optional extra content rendered in the sheet header row, before the
   * "Open full page" link (e.g. per-caller actions).
   */
  headerExtra?: ReactNode;
  /**
   * Called when the sheet is dismissed (X button, ESC, backdrop click, or
   * programmatic close). Lets callers sync external state such as the URL.
   */
  onClose?: () => void;
}

/** Resolve the value used to route to `/content/[id]` (slug preferred, then id). */
function contentRouteId(content: Pick<ApiContent, 'id' | 'slug'>): string {
  return content.slug || String(content.id);
}

/**
 * Opens a content record in a SheetManager sheet, rendering the same
 * ContentDetailView used on the full page. Includes a link to open the
 * standalone `/content/[id]` page.
 *
 * Accepts either a full `ApiContent` (seeds the query cache so the sheet
 * renders instantly) or an id/slug (fetched inside the sheet).
 */
export function useContentDetailSheet() {
  const $ = _c(3);
  const {
    openSheet
  } = useSheetManager();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== openSheet || $[1] !== queryClient) {
    const showContentDetail = (contentOrId, options) => {
      let sheetId;
      let title = "Content";
      let routeId;
      let content;
      if (typeof contentOrId === "object") {
        content = contentOrId;
        routeId = contentRouteId(content);
        title = content.title || "Content";
        sheetId = `content-detail-${content.id}`;
        queryClient.setQueryData(contentQueryKeys.detail(content.id), content);
        if (content.slug) {
          queryClient.setQueryData(contentQueryKeys.detail(content.slug), content);
        }
      } else {
        routeId = String(contentOrId);
        sheetId = `content-detail-${contentOrId}`;
        const cached = queryClient.getQueryData(contentQueryKeys.detail(contentOrId));
        if (cached?.title) {
          title = cached.title;
        }
      }
      openSheet({
        id: sheetId,
        title,
        headerExtra: <div className="flex items-center gap-3">{options?.headerExtra}<Link href={`/content/${routeId}`} className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-bold text-[#211d4f] outline-none hover:underline focus-visible:ring-2 focus-visible:ring-[#211d4f]"><ExternalLink className="size-4" aria-hidden="true" /><span className="hidden sm:inline">Open full page</span></Link></div>,
        content: <ContentDetailLoader contentId={content ? content.id : contentOrId as number | string} options={options} />,
        maxWidth: "sm:max-w-4xl",
        keepOpenOnRouteChange: options?.keepOpenOnRouteChange ?? false,
        onClose: options?.onClose
      });
    };
    t0 = {
      showContentDetail
    };
    $[0] = openSheet;
    $[1] = queryClient;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
function ContentDetailLoader(t0) {
  const $ = _c(7);
  const {
    contentId,
    options
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      refetchOnMount: "always"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: content,
    isLoading
  } = useContentItem(contentId, t1);
  const {
    data: session
  } = useSession();
  const isAdmin = !!session?.user?.isAdmin;
  const showEditButton = options?.showEditButton ?? isAdmin;
  if (isLoading) {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="flex items-center justify-center py-12"><Loader2 className="size-8 animate-spin text-gray-400" /></div>;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  if (!content) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="flex items-center justify-center py-12"><p className="text-gray-500">Content not found</p></div>;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  const t2 = options?.showMap ?? false;
  let t3;
  if ($[3] !== content || $[4] !== showEditButton || $[5] !== t2) {
    t3 = <ContentDetailView content={content} showMap={t2} showEditButton={showEditButton} />;
    $[3] = content;
    $[4] = showEditButton;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}